import { useCropGestureHandlers } from '../use-crop-gesture-handlers'
import { useEditModeChrome } from '../use-edit-mode-chrome'
import { useEditModeCommitSync } from '../use-edit-mode-commit-sync'
import { useNodeEditPathHandlers } from '../use-node-edit-path-handlers'
import { usePenToolSession } from '../use-pen-tool-session'
import type { CanvasEditModes, CanvasFoundation } from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * The handlers the edit-mode chrome calls back into: the pen tool's path session, node-edit path
 * changes and commits, keeping an edit mode in sync when its shape is committed elsewhere, crop
 * frame/pan/rotate gestures, and the chrome geometry for node edit and crop.
 */
export function useCanvasEditModeHandlers(
  {
    isPenToolActive,
    onShapeCommitted,
    onNodeEditModeChange,
    onBatchShapesCommitted,
    onLiveSelectionGeometrySync,
    bleedWidth,
    bleedHeight,
  }: ResolvedCenterCanvasConfig,
  { internalShapeManagerRef }: Pick<CanvasFoundation, 'internalShapeManagerRef'>,
  {
    nodeEditShapeId,
    nodeEditShapeData,
    setNodeEditShapeId,
    setNodeEditShapeData,
    isNodeEditShapeGeometryBlocked,
    nodeEditPreDragShapeRef,
    nodeEditOriginalShapeRef,
    pulseSelectionGeometryBlocked,
    nodeEditShapeIdRef,
    exitNodeEditMode,
    cropShapeIdRef,
    setCropShapeData,
    exitCropMode,
    cropShapeData,
    cropOriginalShapeRef,
    cropGestureBeforeRef,
    isNodeEditMode,
    isCropMode,
  }: Pick<
    CanvasEditModes,
    | 'nodeEditShapeId'
    | 'nodeEditShapeData'
    | 'setNodeEditShapeId'
    | 'setNodeEditShapeData'
    | 'isNodeEditShapeGeometryBlocked'
    | 'nodeEditPreDragShapeRef'
    | 'nodeEditOriginalShapeRef'
    | 'pulseSelectionGeometryBlocked'
    | 'nodeEditShapeIdRef'
    | 'exitNodeEditMode'
    | 'cropShapeIdRef'
    | 'setCropShapeData'
    | 'exitCropMode'
    | 'cropShapeData'
    | 'cropOriginalShapeRef'
    | 'cropGestureBeforeRef'
    | 'isNodeEditMode'
    | 'isCropMode'
  >
) {
  const {
    continuingShapeIdRef,
    penToolInitialPoints,
    penToolOverlayKey,
    setPenToolOverlayKey,
    penToolOverlayRef,
    getPenToolShapeBounds,
    handlePenPathCompleted,
    handleContinuePath,
  } = usePenToolSession({
    internalShapeManagerRef,
    isPenToolActive,
    nodeEditShapeId,
    nodeEditShapeData,
    setNodeEditShapeId,
    setNodeEditShapeData,
    isNodeEditShapeGeometryBlocked,
    onShapeCommitted,
    onNodeEditModeChange,
  })

  const { handleNodeEditPathChange, handleNodeEditPathCommit, handleNodeEditReset } =
    useNodeEditPathHandlers({
      internalShapeManagerRef,
      nodeEditShapeData,
      setNodeEditShapeData,
      nodeEditPreDragShapeRef,
      nodeEditOriginalShapeRef,
      isNodeEditShapeGeometryBlocked,
      pulseSelectionGeometryBlocked,
      onShapeCommitted,
    })

  const { handleShapeCommitted, handleBatchShapesCommitted, handleLiveSelectionGeometrySync } =
    useEditModeCommitSync({
      internalShapeManagerRef,
      nodeEditShapeIdRef,
      setNodeEditShapeData,
      exitNodeEditMode,
      cropShapeIdRef,
      setCropShapeData,
      exitCropMode,
      isNodeEditShapeGeometryBlocked,
      onShapeCommitted,
      onBatchShapesCommitted,
      onLiveSelectionGeometrySync,
    })

  const {
    handleCropFrameChange,
    handleCropFrameCommit,
    handlePhotoPanChange,
    handlePhotoPanCommit,
    handleShapeRotateChange,
    handleShapeRotateCommit,
    handleCropReset,
  } = useCropGestureHandlers({
    internalShapeManagerRef,
    cropShapeData,
    setCropShapeData,
    cropOriginalShapeRef,
    cropGestureBeforeRef,
    onShapeCommitted,
  })

  const { nodeEditChromeShape, nodeEditDimGeom, cropChromeShape, cropDimGeom } = useEditModeChrome({
    isNodeEditMode,
    nodeEditShapeData,
    isCropMode,
    cropShapeData,
    isNodeEditShapeGeometryBlocked,
    bleedWidth,
    bleedHeight,
  })

  return {
    continuingShapeIdRef,
    penToolInitialPoints,
    penToolOverlayKey,
    setPenToolOverlayKey,
    penToolOverlayRef,
    getPenToolShapeBounds,
    handlePenPathCompleted,
    handleContinuePath,
    handleNodeEditPathChange,
    handleNodeEditPathCommit,
    handleNodeEditReset,
    handleShapeCommitted,
    handleBatchShapesCommitted,
    handleLiveSelectionGeometrySync,
    handleCropFrameChange,
    handleCropFrameCommit,
    handlePhotoPanChange,
    handlePhotoPanCommit,
    handleShapeRotateChange,
    handleShapeRotateCommit,
    handleCropReset,
    nodeEditChromeShape,
    nodeEditDimGeom,
    cropChromeShape,
    cropDimGeom,
  }
}
